import { Avatar, Badge, Icon, cn } from '@evogard/ui';
import { count, rankingPodiumOf } from '@evogard/domain';
import type { ConsultantPosition, Period } from '@evogard/contract';
import { MoneyValue, currentPeriod, periodLabel } from './common.js';

/**
 * O pódium do ranking — de um a três degraus, conforme a competência tenha.
 *
 * ## É a única peça do produto que decora uma posição, e isso foi decidido
 *
 * `DESIGN.md` lista "gamificação" entre o que não se faz, e continua listando.
 * O pódium é exceção pedida pelo dono em 10/09/2026 e registrada lá. Nasceu só
 * com a forma — três degraus de alturas diferentes e a medalha como token —, e
 * **em 07/10/2026 ganhou o estilo entregue pelo dono**: degraus em gradiente de
 * metal com uma faixa de brilho que os atravessa em onda, da esquerda para a
 * direita, e os três avatares redondos no metal do lugar — o 1º maior, em
 * laranja, com aro dourado e uma coroa flutuando. O fundo do desenho era navy,
 * e ficou o cinza da casa, a pedido dele. Isso reverte o "sem gradiente nos
 * degraus" de 10/09, e o `DESIGN.md` registra a troca. Continuam valendo: sem
 * bloco navy, sem verde no faturamento e sem foto. As cores e os efeitos moram
 * em `theme.css` (`--cor-podio-*` e as utilidades `podio-*`), e quem pede
 * menos movimento não vê animação nenhuma.
 *
 * ## O que ele recusa fazer é o que o torna honesto
 *
 * Um pódium **afirma** que estas pessoas são a 1ª, a 2ª e a 3ª. O domínio nem
 * sempre sustenta a afirmação inteira, e o defeito não aparece em teste de
 * unidade nenhum — aparece na competência em que duas pessoas empatam:
 *
 *   · `positionInRanking` dá a MESMA posição a quem empata, e a seguinte pula —
 *     1º, 2º, 2º, 4º. Existe competência **sem 3º lugar** e competência **sem
 *     2º**, e nelas o pódium desenha só os degraus que existem;
 *   · quem só fez reativação entra na lista com `achieved: 0`. Numa praça
 *     pequena, um pódium ingênuo coroaria quem não vendeu;
 *   · a lista pode ter uma, duas ou nenhuma pessoa.
 *
 * `consultants.slice(0, 3)` desenharia três degraus em todos esses casos — e
 * desempataria na tela quem o servidor recusou desempatar, que é exatamente o
 * que o docblock de `positionInRanking` manda não fazer.
 *
 * ## Um e dois degraus, e por que eles passaram a existir
 *
 * Até 11/09 ele exigia os três e sumia fora disso. O filtro por time
 * (`sales.ranking:filtro_times`) tornou isso um defeito visível: **toda equipe
 * de uma ou duas pessoas ficava sem pódium**, e um quadro legítimo sem pódium
 * nenhum era a tela negando o que ela mesma acabou de listar logo abaixo.
 *
 * O que a mudança **não** afrouxou é a regra de empate: `rankingPodiumOf` corta
 * no primeiro lugar que não é de uma pessoa só, e nunca pula para o próximo
 * ocupado. Um degrau a menos é uma afirmação a menos; um degrau errado seria
 * uma afirmação falsa.
 *
 * Quem decide isso é **`rankingPodiumOf`, em `@evogard/domain`**, ao lado de
 * `positionInRanking` e com teste. Aqui a regra ficaria sem cobertura nenhuma:
 * `apps/web` não tem teste de unidade de propósito, e esta é a regra que
 * ninguém percebe quebrar — o pódium continua desenhando três degraus bonitos
 * no dia em que dois consultores empatam.
 *
 * Sumir continua sendo a resposta certa quando nem o primeiro degrau existe, e
 * não é desistência: a tabela está logo abaixo, é completa, e diz a mesma coisa
 * sem precisar de degrau nenhum. O pódium é ornamento sobre um dado que a tela
 * já mostra — quando o ornamento não cabe, quem fica é o dado.
 *
 * ## Ele não recalcula nada
 *
 * Nenhum `sort`, nenhuma soma, nenhum formato próprio de dinheiro: `position`
 * vem do servidor, `MoneyValue` é o mesmo da tabela e a contagem descartada usa
 * a mesma frase de `Counted`. Duas leituras do mesmo número na mesma tela é o
 * defeito que este módulo inteiro existe para não repetir — a operação já viveu
 * com 33 numa tela e 37 na outra.
 */
export function Podium({
  consultants,
  period,
}: {
  consultants: readonly ConsultantPosition[];
  period: Period;
}) {
  const podium = rankingPodiumOf(consultants);
  if (!podium) return null;
  /*
   * "Em andamento" só na competência corrente: é o mês em que o quadro ainda
   * muda a cada venda. Não diz "ao vivo" — a tela não se atualiza sozinha, e o
   * selo afirmaria o que o código não cumpre.
   */
  const ongoing = period === currentPeriod();

  return (
    /*
     * `sm:` e não `md:`: abaixo disso o pódium não existe, e é de propósito. O
     * desenho de origem pedia "vertical_ranking" no telefone — e isso já é a
     * tabela que vem logo abaixo. Renderizar os dois seria a mesma lista duas
     * vezes na mesma rolagem, com a de cima cabendo três pessoas e a de baixo
     * todas.
     */
    /*
     * O palco é o cinza da casa (`superficie-suave`), e não o navy do desenho
     * de origem — decisão do dono em 07/10. O `mb-6` é o respiro antes da
     * tabela: colado nela, o pódium lia como o cabeçalho dela.
     */
    <div className="mx-4 mt-4 mb-6 hidden rounded-cartao bg-superficie-suave px-6 pt-5 sm:block">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-xl font-bold">Pódio</h3>
        <span className="text-sm text-texto-suave">{periodLabel(period)}</span>
        {ongoing && <Badge tone="progresso">Em andamento</Badge>}
      </div>

      <ol className="flex items-end justify-center gap-4 pt-10">
        {podium.map((person, index) => (
          <Step key={person.consultant.id} place={PLACES[index] ?? 3} person={person} />
        ))}
      </ol>
    </div>
  );
}

/**
 * `rankingPodiumOf` devolve os degraus em ordem de colocação; aqui o índice
 * vira o número do lugar.
 *
 * A lista tem de um a três elementos, e o índice é a colocação **consecutiva**
 * — quem está em `podium[1]` é o 2º, sempre. É `rankingPodiumOf` que garante
 * isso ao cortar no primeiro buraco — os lugares desenhados são sempre 1, 2 e 3
 * consecutivos. Sem essa garantia, o índice e a colocação deixariam de
 * coincidir e alguém usaria um selo que não é dela.
 */
const PLACES = [1, 2, 3] as const;

/** A altura de cada degrau — 170, 120 e 90px, as do desenho de 07/10. */
const HEIGHTS = { 1: 'h-42.5', 2: 'h-30', 3: 'h-22.5' } as const;

/**
 * O 1º no meio, o 2º à esquerda, o 3º à direita — pelo `order` do flex, e não
 * pela ordem do DOM.
 *
 * **Com menos de três degraus a regra é a mesma**, e o arranjo é o pódium com
 * os degraus que faltam removidos: com dois, o 2º fica à esquerda e o 1º à
 * direita; com um, ele fica sozinho no centro, porque a `<ol>` é
 * `justify-center`. Dar uma ordem própria a cada tamanho faria o mesmo dado
 * mudar de lugar conforme quantas pessoas o time tem — e quem olha duas
 * competências seguidas leria isso como troca de posição.
 *
 * A ordem do DOM é 1º, 2º, 3º, que é o que um leitor de tela precisa ouvir de
 * uma `<ol>` — e é o que a lista AFIRMA ser. Trocar os elementos de lugar no
 * JSX para o 1º cair no meio faria a marcação dizer que o segundo colocado é o
 * primeiro item de uma lista ordenada. O pódium é arranjo visual; a ordem é
 * fato.
 */
const ORDERS = { 1: 'order-2', 2: 'order-1', 3: 'order-3' } as const;

/**
 * As três cores de colocação: o selo pelos tokens `gold`, `silver` e `bronze`;
 * o avatar (preenchimento, aro e halo) e o metal do degrau pelos
 * `--cor-podio-*` e pelas utilidades `podio-*` de `theme.css`.
 *
 * Escritos por extenso porque o Tailwind varre o código como TEXTO: `bg-${cor}`
 * nunca é gerado, e a classe some da folha sem erro nenhum.
 */
const MEDALS = {
  1: {
    avatar: 'size-19 border-podio-aro bg-podio-avatar podio-aura-ouro',
    badge: 'bg-gold',
    metal: 'podio-ouro podio-onda-1',
  },
  2: {
    avatar: 'size-16 border-podio-prata bg-podio-prata-escuro podio-aura-prata',
    badge: 'bg-silver',
    metal: 'podio-prata podio-onda-2',
  },
  3: {
    avatar: 'size-16 border-podio-bronze-claro bg-podio-bronze podio-aura-bronze',
    badge: 'bg-bronze',
    metal: 'podio-bronze podio-onda-3',
  },
} as const;

type Place = 1 | 2 | 3;

/**
 * Um degrau: a pessoa por cima, o bloco por baixo.
 *
 * O bloco não carrega texto nenhum. Ele é altura — e altura é a única coisa
 * neste componente que diz "este ficou na frente daquele" sem depender de cor,
 * o que faz o pódium continuar legível para quem não distingue ouro de bronze.
 * A cor confirma; ela nunca é o único sinal, e o número está escrito no selo.
 */
function Step({ place, person }: { place: Place; person: ConsultantPosition }) {
  const medal = MEDALS[place];

  return (
    <li
      className={cn('flex w-full max-w-56 min-w-0 flex-col items-center', ORDERS[place])}
    >
      {/*
       * A coroa só no 1º, flutuando acima do avatar. Ela é enfeite: o "1º" do
       * selo continua sendo quem diz a colocação, e o leitor de tela não a ouve.
       */}
      {place === 1 && (
        <Icon nome="coroa" size={24} className="podio-coroa mb-1 text-podio-coroa" />
      )}
      <div className="relative">
        {/*
         * Os três no mesmo formato — redondo, preenchido com o metal do lugar,
         * aro mais claro e halo —, e o 1º um pouco maior e com a coroa. As
         * iniciais são a `podio-tinta`, que se lê sobre os três metais.
         */}
        <Avatar
          nome={person.consultant.user.nome}
          size={place === 1 ? 'xl' : 'lg'}
          className={cn('rounded-full border-3 text-podio-tinta', medal.avatar)}
        />
        {/*
         * O selo fica FORA do fluxo, encostado no canto do avatar, e leva o
         * mesmo "1º" que a coluna Posição da tabela usa. O desenho de origem
         * pedia um troféu no primeiro lugar e números nos outros dois; um
         * símbolo diferente no topo faria a leitura do quadro depender de saber
         * que troféu quer dizer 1, e o quadro de baixo já responde em número.
         */}
        <span
          className={cn(
            'absolute -end-1.5 -bottom-0.5 grid size-7 place-items-center',
            'rounded-pilula text-xs font-bold tabular-nums text-texto-inverso',
            medal.badge,
          )}
        >
          {place}º
        </span>
      </div>

      <p className="mt-3 w-full truncate text-center text-sm font-semibold">
        {person.consultant.user.nome}
      </p>
      <p className="mt-0.5 text-xs tabular-nums text-texto-suave">
        {count(person.achieved, 'venda', 'vendas')}
      </p>
      {/*
       * O descarte da contagem **não** aparece aqui, nem na tabela abaixo.
       *
       * Ele apareceu até 10/09 — "4 reativações fora" sob o número —, e saiu
       * quando o detalhe passou a existir: repetir em três lugares do mesmo
       * cartão um número que ninguém consegue conferir ali é o que ele tem de
       * pior. Quem quer a diferença abre a linha e vê **quais** placas ficaram
       * de fora, uma a uma, em vez de só quantas.
       *
       * O campo continua vindo do servidor: é ele que compõe o rótulo do botão
       * que abre o detalhe ("Ver as 37 vendas de …"), e é ele que o modal
       * mostra ao lado do realizado.
       */}
      <p className="mt-1 text-sm font-medium">
        <MoneyValue value={person.value} />
      </p>

      {/*
       * O degrau é o metal em gradiente, com a faixa de brilho passando por
       * cima (`podio-degrau`, um `::after`). Continua sem texto nenhum: a
       * altura é o que diz quem ficou na frente sem depender de cor.
       */}
      <div className={cn('podio-degrau mt-3 w-full', HEIGHTS[place], medal.metal)} />
    </li>
  );
}
